import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listUserPolicyExceptions,
  createUserPolicyException,
  updateUserPolicyException,
  deleteUserPolicyException,
} from '@kirin/api-client/api/policy-exceptions';
import type {
  CreatePolicyException,
  UpdatePolicyException,
  PolicyExceptionAction,
} from '@kirin/api-client/contracts/policy-exceptions';
import { ApiError } from '@kirin/api-client/errors';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from './queryKeys';
import { retryHandlers } from './helpers';

/**
 * Hook to fetch allow/block rules for the current user
 *
 * @param policyKey - The policy key to fetch rules for
 * @param action - Optional filter by action type (allow/block)
 * @param enabled - Whether to enable the query
 */
export function useUserPolicyExceptions(
  policyKey: string | undefined,
  action?: PolicyExceptionAction,
  enabled = true
) {
  const isEnabled = enabled && !!policyKey;
  return useQuery({
    queryKey: queryKeys.policyExceptions.user(policyKey ?? '', action),
    queryFn: async () => {
      if (!policyKey) throw new Error('policyKey is required');
      const result = await listUserPolicyExceptions(
        apiClient,
        policyKey,
        action
      );
      return result.exceptions;
    },
    enabled: isEnabled,
    retry: retryHandlers.list,
  });
}

/**
 * Hook to create an allow/block rule for the current user
 *
 * @param policyKey - The policy key to create the rule for
 */
export function useCreateUserPolicyException(policyKey: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreatePolicyException) => {
      if (!policyKey) throw new Error('Policy key is required');
      return createUserPolicyException(apiClient, policyKey, data);
    },
    onSuccess: () => {
      if (policyKey) {
        void queryClient.invalidateQueries({
          queryKey: ['policyExceptions', 'user', policyKey],
        });
      }
    },
  });
}

/**
 * Hook to update an allow/block rule for the current user
 *
 * @param policyKey - The policy key the rule belongs to
 */
export function useUpdateUserPolicyException(policyKey: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      exceptionId,
      data,
    }: {
      exceptionId: string;
      data: UpdatePolicyException;
    }) => {
      if (!policyKey) throw new Error('Policy key is required');
      return updateUserPolicyException(apiClient, policyKey, exceptionId, data);
    },
    onSuccess: () => {
      if (policyKey) {
        void queryClient.invalidateQueries({
          queryKey: ['policyExceptions', 'user', policyKey],
        });
      }
    },
  });
}

/**
 * Hook to delete an allow/block rule for the current user
 *
 * @param policyKey - The policy key the rule belongs to
 */
export function useDeleteUserPolicyException(policyKey: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (exceptionId: string) => {
      if (!policyKey) throw new Error('Policy key is required');
      try {
        return await deleteUserPolicyException(
          apiClient,
          policyKey,
          exceptionId
        );
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
          return;
        }
        throw error;
      }
    },
    onSuccess: () => {
      if (policyKey) {
        void queryClient.invalidateQueries({
          queryKey: ['policyExceptions', 'user', policyKey],
        });
      }
    },
  });
}
